/* eslint-disable no-spaced-func */
import React from 'react';
import { PostInsights } from '@frontend/gql/social/graphql';
import { useSocialMediaThumbnail } from '@frontend/applications/Social/hooks/useSocialMediaThumbnail';
import { HandWaveIcon, EyeIcon, CalendarIcon } from '@revfluence/fresh-icons/regular/esm';
import { StarsIcon } from '@revfluence/fresh-icons/solid/esm';
import { Badge } from '@frontend/shadcn/components/ui/badge';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@frontend/shadcn/components/ui/tooltip';
import { formatDate } from '@frontend/applications/Social/utils/shared/date-helpers';
import {
  PostCard,
  PostCardMedia,
  PostCardSkeleton,
  PostCardBackground,
  PostCardContent,
  PostCardImageContainer,
  PostCardImageContent,
  PostCardNetworkIcon,
  PostCardImage,
  PostCardPlaceholder,
  PostCardGradient,
  PostCardHeader,
  PostCardAvatar,
  PostCardAvatarImage,
  PostCardAvatarFallback,
  PostCardAvatarName,
  PostCardFooter,
  PostCardActions,
  PostCardStats,
  PostCardStat,
  PostCardAvatarContainer,
  PostCardCheckbox,
  PostCardHeaderRow,
  PostCardStatText,
} from '@frontend/applications/Social/components/post-card';

type PostTableRow = Omit<PostInsights, 'memberId' | 'accountEntityId'>;

/* ---------------------------------- Types ---------------------------------- */

type PostInsightsCardProps = {
  post: PostInsights;
  onPostClick?: (post: PostInsights) => void;
  showEngagement?: boolean;
  selectedColumns?: string[];
  showDates?: boolean;
  defaultOpenImage?: boolean;
  removeSelectionCheckbox?: boolean;
  className?: string;
  isSelected?: boolean;
  onSelectionChange?: (post: PostTableRow, selected: boolean) => void;
};

/* ----------------------------- PostCard Component ---------------------------- */

const PostInsightsCard = React.forwardRef<HTMLDivElement, PostInsightsCardProps>(
  ({ post, isSelected, onSelectionChange, removeSelectionCheckbox, ...props }, ref) => {
    const member = post.member;
    const total = post.total;
    const memberName = member?.name || '';
    // parse tags from post.postCaption
    const tags = post.postCaption?.match(/#([\w_]+)/g) || [];

    // get the first 3 tags
    const tagsVisible = tags.slice(0, 3);
    const { imageUrl, isLoading, onImageError } = useSocialMediaThumbnail({
      postUrl: post.postPermalink ? post.postPermalink : '',
      postImageUrl: post.postImageUrl ? post.postImageUrl : '',
    });

    const showLoading = isLoading;
    const showImage = imageUrl && !showLoading;
    const showPlaceholder = !showImage && !showLoading;

    return (
      <PostCard ref={ref} {...props}>
        <PostCardMedia>
          {showLoading && <PostCardSkeleton />}
          {showImage && (
            <>
              <PostCardBackground imageUrl={imageUrl} />
              <PostCardImageContainer>
                <PostCardImageContent>
                  <PostCardImage src={imageUrl} onError={onImageError} />
                </PostCardImageContent>
              </PostCardImageContainer>
            </>
          )}
          {showPlaceholder && <PostCardPlaceholder />}
          <PostCardGradient />
        </PostCardMedia>

        <PostCardContent>
          <PostCardHeader>
            <PostCardHeaderRow>
              <PostCardAvatarContainer>
                <PostCardAvatar networkIcon={<PostCardNetworkIcon network={post.entityNetwork} />}>
                  <PostCardAvatarImage src={member?.profilePicture || undefined} alt={memberName} />
                  <PostCardAvatarFallback name={memberName} />
                </PostCardAvatar>
                <PostCardAvatarName>{memberName}</PostCardAvatarName>
              </PostCardAvatarContainer>
              {removeSelectionCheckbox !== true && <PostCardCheckbox
                checked={isSelected}
                onClick={(e) => e.stopPropagation()}
                onCheckedChange={() => onSelectionChange?.(post, !isSelected)}
                data-dd-action-name="social-analytics-post-checkbox-toggle"
              />}
            </PostCardHeaderRow>
          </PostCardHeader>

          <div className="flex-grow" />

          <PostCardFooter>
            {/* Boost Eligible Badge */}
            {post.advertisableMediaPermissions?.some((permission) => permission?.hasPermissions) && (
              <div className="flex justify-start">
                <Badge variant="secondary" className="bg-green-4 text-white text-xs px-2 py-1 flex items-center gap-1">
                  <StarsIcon className="h-3 w-3" />
                  Boost Eligible
                </Badge>
              </div>
            )}
            {/* Tags Row - Demo data for now - only show if post has caption */}
            {tags.length > 0 && (
              <div className="flex flex-wrap items-center gap-1">
                {tagsVisible.map((tag) => (
                  <Badge key={tag} variant="outline" className="text-xs px-2 py-1 bg-grey-2 text-grey-9 border-grey-4">
                    {tag}
                  </Badge>
                ))}
                {tagsVisible.length < tags.length && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <Badge variant="outline" className="text-xs px-2 py-1 bg-grey-2 text-grey-9 border-grey-4">
                          +{tags.length - tagsVisible.length}
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{tags.slice(tagsVisible.length).join(', ')}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            )}
            {/* Date and Stats Row */}
            <PostCardActions>
              {/* Date posted */}
              <PostCardStatText
                icon={<CalendarIcon className="h-4 w-4" />}
                text={formatDate(post.postDate)}
              />

              {/* Stats */}
              <PostCardStats>
                {/* Always show impressions (views in design) */}
                {total?.impressions != null && (
                  <PostCardStat icon={<EyeIcon className="h-4 w-4" />} value={total.impressions} />
                )}
                {/* Show engagement on sm+ screens */}
                {total?.engagements != null && (
                  <PostCardStat
                    icon={<HandWaveIcon className="h-4 w-4" />}
                    value={total.engagements}
                    className="hidden sm:flex"
                  />
                )}
              </PostCardStats>
            </PostCardActions>
          </PostCardFooter>
        </PostCardContent>
      </PostCard>
    );
  },
);
PostInsightsCard.displayName = 'PostCard';

export default PostInsightsCard;
